// RC10s · Driver assignment preflight client.
//
// Single entry point that both the Find Drivers modal and the
// Co-Driver / Fill-In picker use. Shares the `correlation_id`
// across the preflight-on-select and the atomic save rerun inside
// one assignment attempt.
import { api } from "./api";

/**
 * Fire a preflight against the canonical server evaluator.
 *
 * @param {string} tourId
 * @param {string} driverId
 * @param {string} assignmentRole — primary_driver | co_driver | fill_in
 * @param {string} [correlationId] — reuse to continue a family
 * @returns {Promise<{
 *   eligible: boolean,
 *   blocking_reasons: Array<{code: string, message: string, resolution: string, details: object}>,
 *   warnings: Array<{code: string, message: string, resolution: string, details: object}>,
 *   correlation_id: string,
 * }>}
 */
export async function preflight(tourId, driverId, assignmentRole, correlationId) {
  const body = {
    driver_id: driverId,
    assignment_role: assignmentRole,
  };
  if (correlationId) body.correlation_id = correlationId;
  try {
    const r = await api.post(`/tours/${tourId}/driver-assignment-preflight`, body);
    return r.data;
  } catch (err) {
    // Server unexpectedly 500 — body still carries the structured
    // envelope. Return it so the UI can render a safe
    // UNEXPECTED_ASSIGNMENT_ERROR card.
    const detail = err?.response?.data?.detail;
    if (detail && typeof detail === "object" && Array.isArray(detail.blocking_reasons)) {
      return detail;
    }
    return {
      eligible: false,
      blocking_reasons: [{
        code: "UNEXPECTED_ASSIGNMENT_ERROR",
        message: "Something prevented us from checking this assignment. Operations has been notified.",
        resolution: "Please try again. If the problem persists, contact support.",
        details: {},
      }],
      warnings: [],
      correlation_id: correlationId || `rc10s-local-${Math.random().toString(36).slice(2, 10)}`,
    };
  }
}

/**
 * Parse the backend-return error payload from a POST /tours/.../drivers
 * or PATCH /tours/.../drivers/{id} failed save and shape it to match
 * the preflight response envelope. Used when the atomic save rerun
 * returns 409 with the shared RC10s dialog data.
 */
export function parseSaveErrorAsDiagnostic(err, fallbackCorrelationId) {
  const detail = err?.response?.data?.detail;
  if (detail && typeof detail === "object") {
    const correlation_id =
      detail.correlation_id ||
      fallbackCorrelationId ||
      `rc10s-local-${Math.random().toString(36).slice(2, 10)}`;
    // RC10s shape — already correct.
    if (Array.isArray(detail.blocking_reasons)) {
      return {
        eligible: !!detail.eligible,
        blocking_reasons: detail.blocking_reasons,
        warnings: detail.warnings || [],
        correlation_id,
      };
    }
    // Legacy RC10m / DAQ-1 shape — translate.
    if (detail.code === "medical_expiry_acknowledgment_required") {
      return {
        eligible: false,
        blocking_reasons: [],
        warnings: [{
          code: "MEDICAL_CARD_EXPIRES_DURING_TOUR",
          message: detail.message,
          resolution: "Acknowledge the warning to proceed.",
          details: {
            medical_card_expiration: detail.medical_card_expiration,
            tour_start_date: detail.tour_start_date,
            tour_end_date: detail.tour_end_date,
          },
        }],
        correlation_id,
        _medical_ack_required: true,
      };
    }
    if (detail.code === "driver_ineligible") {
      // Legacy block_reasons[] translation.
      const legacyBlocks = (detail.block_reasons || []).map((r) => ({
        code: ({
          MEDICAL_EXPIRED: "MEDICAL_CARD_EXPIRED_BEFORE_TOUR",
          LICENSE_EXPIRED: "CDL_EXPIRED_BEFORE_TOUR",
          UNAVAILABLE: "DRIVER_EXPLICITLY_UNAVAILABLE",
          ASSIGNMENT_CONFLICT: "DRIVER_ALREADY_ASSIGNED",
          RESTRICTED: "DRIVER_RESTRICTED_FOR_CUSTOMER",
          INACTIVE: "DRIVER_INACTIVE",
        })[r.code] || r.code || "UNEXPECTED_ASSIGNMENT_ERROR",
        message: r.message || "This driver cannot be assigned.",
        resolution: r.resolution || "",
        details: r.context || r.details || {},
      }));
      return {
        eligible: false,
        blocking_reasons: legacyBlocks,
        warnings: [],
        correlation_id,
      };
    }
    if (detail.code === "duplicate" || detail.code === "primary_as_co_driver") {
      return {
        eligible: false,
        blocking_reasons: [{
          code: "DUPLICATE_TOUR_ASSIGNMENT",
          message: detail.message || "This driver is already assigned to this Tour.",
          resolution: "Choose a different driver or remove the existing assignment first.",
          details: {},
        }],
        warnings: [],
        correlation_id,
      };
    }
  }
  // Last resort: safe generic card. Never return raw error text.
  return {
    eligible: false,
    blocking_reasons: [{
      code: "UNEXPECTED_ASSIGNMENT_ERROR",
      message: "Something prevented this assignment. Operations has been notified.",
      resolution: "Please try again. If the problem persists, contact support.",
      details: {},
    }],
    warnings: [],
    correlation_id: fallbackCorrelationId || `rc10s-local-${Math.random().toString(36).slice(2, 10)}`,
  };
}

export function newLocalCorrelationId() {
  return `rc10s-local-${Math.random().toString(36).slice(2, 14)}`;
}
